import { EntityViewRecordScope, MetadataEntityData, MetadataEntityFieldData, resolveParentLevels } from '@jms/model';

/** Option group the parents are listed under, above the row entity's own fields. */
export const GROUP_PARENT_FIELD_GROUP = 'Parent';

/**
 * The one grouping pool, shared by the kanban Row axis and both table Group dropdowns: each ancestor
 * of the row grain as a parent option, then the `groupable` fields.
 *
 * Grouping takes every groupable field, bounded or not — a group is data-driven from the loaded page,
 * so grouping by an open field (a technician, a company) costs one group per value present rather than
 * a group per value that could exist. Kanban COLUMNS are the narrower pool, because they enumerate the
 * whole value set up front.
 *
 * Grouping by a parent is the parent as a concept — the user picks "Job", not "Job Number". The
 * option still carries the identifier path as its id, because that is what `kanban.row` / `table.groupBy`
 * store and what the server accepts as a paging groupBy; only the label is the parent's name. Deriving
 * the parents from the secondaryFilter chain means no entity has to flag an identifier that is only ever
 * an axis when something is unwound beneath it.
 *
 * The table was fixed to None | Job at Secondary scope while only parent grain paged whole groups.
 * The server now pages whole row-level groups too (`mongo.row.group.paging.ts`), so a Stages board can
 * group by a stage field without a group straddling a page — the two axes take the same pool.
 */
export function resolveGroupFields(metadata: MetadataEntityData | null | undefined, scope: EntityViewRecordScope | string | null | undefined, uiFields: MetadataEntityFieldData[]): MetadataEntityFieldData[] {

  const groupable = uiFields.filter(f => !!f.groupable);
  const parents = resolveParentLevels(metadata, scope);
  if (!parents.length) return groupable;

  // A parent is offered once, as the parent — an identifier that is also groupable must not
  // appear a second time under its own field name.
  const parentPaths = new Set(parents.map(p => p.identifierPath));

  return [
    ...parents.map(parent => toParentFieldOption(parent.identifierPath, parent.displayName, uiFields)),
    ...groupable.filter(f => !parentPaths.has(f.path ?? f.name)),
  ];
}

/**
 * The parent option as a field the settings panel can render: the real identifier field where one is
 * in scope (so type/icon/format survive), relabelled to the parent entity and moved to the Parent
 * group. Cloned rather than mutated — `uiFields` is the shared metadata projection.
 */
function toParentFieldOption(identifierPath: string, displayName: string, uiFields: MetadataEntityFieldData[]): MetadataEntityFieldData {

  const source = uiFields.find(f => (f.path ?? f.name) === identifierPath);
  return { ...(source ?? {}), path: identifierPath, name: identifierPath, displayName, group: GROUP_PARENT_FIELD_GROUP } as MetadataEntityFieldData;
}
